import * as React from "react";
import { Col, Modal, Row } from "react-bootstrap";
import { connect } from "react-redux";
import { compose } from "redux";
import { reduxForm } from "redux-form";
import * as _ from "underscore";

import { DocTypes } from "@eave/shared/types/api";
import APIClient from "../APIClient";
import DocumentUploader from "../DocumentUploader";
import LabeledInput from "../LabeledInput";
import ModalHeader from "../shared/Modal/ModalHeader";
import ModalFooter from "../shared/Modal/ModalFooter";
import { mapStateToProps } from "./selectors";
import { borrowerEntitySelectOptions } from "@eave/shared/utils/borrowers";

const styles = require("./styles.module.scss");

/* AdditionalIncome */
interface IAdditionalIncomeProps {
  application: any;
  additionalIncomeID?: number;
  borrowerEntity?: number;
  handleSubmit: Function;
  onHide: () => void;
}

class AdditionalIncome extends React.Component<IAdditionalIncomeProps, any> {
  constructor(props) {
    super(props);
    let additionalIncome = null;
    if (props.additionalIncomeID) {
      additionalIncome = _.findWhere(props.application.additional_incomes, {
        id: props.additionalIncomeID,
      });
    }

    this.handleSaveClick = this.handleSaveClick.bind(this);
    this.handleTaxClick = this.handleTaxClick.bind(this);

    const borrowerEntity = props.borrowerEntity || null;

    this.state = {
      amount: !additionalIncome ? "" : additionalIncome.monthly_income,
      desc: !additionalIncome ? "" : additionalIncome.description,
      entity: !additionalIncome ? borrowerEntity : additionalIncome.borrower_entity,
      needsToBeCreated: !additionalIncome,
      needsToBeSaved: !additionalIncome,
      showDropzone: false,
      additionalIncomeID: props.additionalIncomeID,
    };
  }

  componentDidMount() {
    const { needsToBeCreated, additionalIncomeID } = this.state;

    if (!additionalIncomeID && needsToBeCreated) {
      this.createAdditionalIncome({}).then((resp: any) => {
        if (resp && resp.additional_income && resp.additional_income.id) {
          this.setState({
            additionalIncomeID: resp.additional_income.id,
            needsToBeCreated: false,
          });
        }
      });
    }
  }

  componentWillUnmount() {
    const { needsToBeCreated, needsToBeSaved, additionalIncomeID } = this.state;

    if (needsToBeSaved && !needsToBeCreated) {
      APIClient.deleteRequest(`/additional_income/${additionalIncomeID}`);
    }
  }

  public onChange(e) {
    const { name, value } = e.target;

    this.setState({
      [name]: value,
    });
  }

  public onChangeSelect(e) {
    if (!e) {
      return;
    }

    this.setState({
      entity: e.value,
    });
  }

  public handleTaxClick(ev) {
    this.setState({
      showDropzone: ev.target.value === "false",
    });
  }

  private additionalIncomePayload(values) {
    const { application } = this.props;
    const { entity } = this.state;

    const payload = {
      borrower_entity: entity,
      description: values.desc,
      loan_application_id: application.id,
      monthly_income: values.amount ? values.amount.replace(/,/g, "") : 0,
      is_on_tax_returns: values.is_on_tax_returns === "true",
    };

    return payload;
  }

  private createAdditionalIncome(values) {
    const payload = this.additionalIncomePayload(values);

    return APIClient.post("/additional_income", payload);
  }

  private updateAdditionalIncome(values) {
    const { additionalIncomeID } = this.state;

    const payload = this.additionalIncomePayload(values);

    return APIClient.put(`/additional_income/${additionalIncomeID}`, payload);
  }

  public handleSaveClick(values) {
    const { onHide } = this.props;
    const { needsToBeCreated } = this.state;

    if (needsToBeCreated) {
      this.createAdditionalIncome(values).then(() => {
        this.setState({ needsToBeSaved: false, needsToBeCreated: false }, onHide);
      });
    } else {
      this.updateAdditionalIncome(values).then(() => {
        this.setState({ needsToBeSaved: false }, onHide);
      });
    }
  }

  public borrowersToOptionsArray() {
    const { borrowers } = this.props.application;
    return borrowerEntitySelectOptions(borrowers);
  }

  public render() {
    const { onHide, application } = this.props;
    const { entity, desc, amount } = this.state;

    return (
      <Modal className="full-screen" show dialogClassName="add-account-modal" onHide={onHide}>
        <ModalHeader onClick={onHide} />

        <Modal.Body>
          <Row className={styles.container}>
            <Col md={6} mdOffset={3}>
              <h1 className={styles.title}>Add income source</h1>
              <p className={styles.subtitle}>
                No need to include rental income here! We'll be asking more on that later.
              </p>

              <LabeledInput
                className="form-control--textarea"
                labelValue="Description"
                inputType="validateTextarea"
                inputName="desc"
                inputValue={desc}
                onChange={this.onChange.bind(this)}
              />

              {!this.state.entity && (
                <LabeledInput
                  labelValue="Income belongs to"
                  inputType="validateSelect"
                  inputName="entity"
                  inputValue={entity}
                  options={this.borrowersToOptionsArray.bind(this)()}
                  onChange={this.onChangeSelect.bind(this)}
                />
              )}

              <LabeledInput
                labelValue="Amount per month"
                inputType="validateMoney"
                inputName="amount"
                inputValue={amount}
                onChange={this.onChange.bind(this)}
              />

              <div className={styles.section}>
                <p className={styles.prompt}>Does this income show on your tax returns?</p>
                <div className={styles.radiobuttons}>
                  <div className={styles.radio}>
                    <LabeledInput
                      inputType="validateRadio"
                      inputName="is_on_tax_returns"
                      radioLabel="Yes"
                      inputValue
                      onChange={this.handleTaxClick}
                    />
                  </div>
                  <div className={styles.radio}>
                    <LabeledInput
                      className={styles.radio}
                      inputType="validateRadio"
                      inputName="is_on_tax_returns"
                      radioLabel="No"
                      inputValue={false}
                      onChange={this.handleTaxClick}
                    />
                  </div>
                </div>
              </div>

              {this.state.showDropzone && (
                <div>
                  <p>Upload a document that details the source of this income.</p>
                  <DocumentUploader
                    applicationID={application.id}
                    documentType={DocTypes.IncomeOther}
                    ownerEntityType="additional_income"
                    ownerEntityID={this.state.additionalIncomeID}
                    prompt="Drag and drop or click here to upload supporting documents"
                    borrowerEntity={entity}
                  />
                </div>
              )}

              <p>
                Any income that appears on your tax returns may not require any more documentation.
              </p>
            </Col>
          </Row>
        </Modal.Body>
        <ModalFooter
          onCancelClick={onHide}
          onSaveClick={this.props.handleSubmit(this.handleSaveClick)}
        />
      </Modal>
    );
  }
}

export default compose<any, any, any>(
  connect(mapStateToProps),
  reduxForm({
    form: "additional-income",
  }),
)(AdditionalIncome);
